/* eslint-disable react/prop-types, react/destructuring-assignment */
import { Toggle } from '@augmentt-dev/ui-components'
import { Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Select from 'react-select'
import { selectStyle } from '../../../../../../_augmentt/utils/styles'
import ScrollMenu from '../../../../../partials/content/ScrollMenu'
import { getFormikSchema } from '../../config'
import states from '../../states.json'
import { Option, checkSelection, cloneChildComponents, formatEmployeeOptions } from '../../utils/helpers'

function ResetPasswordRenderBody(props) {
  const { children, ...rest } = props

  const {
    intl,
    show,
    state,
    action,
    actionData,
    setActionData,
    initialValues,
    onActionSubmit,
    taskParameters,
    selectedCustomer,
    selectedEmployee,
    selectedEmployeeReference,
    setShowFindEmployeeModal,
    allAugmenttEmployees,
    setDisableSubmit,
    setCompletedActions,
  } = rest

  const formikRef = useRef()

  const [employeeOptions, setEmployeeOptions] = useState([])
  const [isAutoGeneratingPassword, setAutoGeneratingPassword] = useState(true)
  const [isSendingPasswordToMe, setSendingPasswordToMe] = useState(true)

  const RESET_PASSWORD_ACTION_ID = 'ENGAGE.RESET_PASSWORD_SUCCESS'

  // Handles enabling/disabling submit button in footer
  useEffect(() => {
    if (!isAutoGeneratingPassword && !actionData?.password) {
      setDisableSubmit(true)
    } else if (!isSendingPasswordToMe && !actionData?.employee) {
      setDisableSubmit(true)
    } else {
      setDisableSubmit(false)
    }
    // eslint-disable-next-line
  }, [isAutoGeneratingPassword, isSendingPasswordToMe, actionData])

  // Handles data setup on initial render
  useEffect(() => {
    if (state !== states.RENDER || show === false) {
      return
    }

    formatEmployeeOptions(allAugmenttEmployees, intl, setEmployeeOptions)
    // eslint-disable-next-line
  }, [])

  // Formik schema
  const schemaOptions = {
    isAutoGeneratingPassword,
  }
  const schema = getFormikSchema(action, intl, schemaOptions)

  // Render
  return (
    <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
      <Formik
        innerRef={formikRef}
        validationSchema={schema || undefined}
        initialValues={initialValues || undefined}
        initialErrors={schema?.isValidSync(initialValues) ? schema.isValidSync(initialValues) : false}
        onSubmit={(values) => {
          // Add the action id to actionCompleted for the success body
          setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, RESET_PASSWORD_ACTION_ID])

          let parameters = taskParameters

          if (typeof taskParameters === 'function') {
            parameters = taskParameters(selectedEmployee, values) // Callback set in Data Loader
          }

          // Fire off the task
          onActionSubmit(action, parameters)
        }}
      >
        {({ handleChange, setFieldValue, setFieldTouched, values, errors, touched }) => (
          <Form>
            <Modal.Body className="sidebar-body">
              <Row>
                <Col sm="12" md="12" lg="12">
                  <div id="passwordToggle" className="flex mb-2">
                    <p
                      className="pe-3"
                      style={{
                        color: isAutoGeneratingPassword ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                      }}
                    >
                      <FormattedMessage id="ENGAGE.AUTO_GENERATE_PASSWORD" />
                    </p>
                    <Toggle
                      onChange={() => {
                        // eslint-disable-next-line no-unused-expressions
                        isAutoGeneratingPassword ? setAutoGeneratingPassword(false) : setAutoGeneratingPassword(true)
                      }}
                      checked={!isAutoGeneratingPassword}
                      className="switch"
                      labelText=""
                      ariaLabel="password-toggle"
                    />
                    <p
                      className="ps-3"
                      style={{
                        color: !isAutoGeneratingPassword ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                      }}
                    >
                      <FormattedMessage id="ENGAGE.MANUALLY_CREATE_PASSWORD" />
                    </p>
                  </div>

                  {!isAutoGeneratingPassword && (
                    <Form.Group id="manualPassword">
                      <Form.Control
                        type="text"
                        className="inline"
                        name="newPassword"
                        placeholder={
                          intl
                            ? intl.formatMessage({
                                id: 'PLACEHOLDER.ENTER_PASSWORD',
                              })
                            : ''
                        }
                        value={values?.newPassword}
                        onChange={(e) => {
                          handleChange(e)
                          setActionData({
                            ...actionData,
                            password: e.target.value,
                          })
                        }}
                        onBlur={(e) => {
                          const val = (e.target.value || '').replace(/\s+/gi, ' ')
                          setFieldValue('newPassword', val.trim())
                          setFieldTouched('newPassword', true, false)
                        }}
                        isInvalid={touched.newPassword && errors.newPassword}
                        isValid={touched.newPassword && !errors.newPassword}
                      />
                      <Form.Control.Feedback style={{ width: '75%' }} className="feedback-control" type="invalid">
                        {errors.newPassword}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}

                  <Form.Group id="requirePasswordChange">
                    <Form.Check
                      className="custom-checkbox-reset-password"
                      custom
                      name="requirePasswordChange"
                      value={values?.changePasswordAtNextLogin}
                      type="checkbox"
                      id="requirePasswordChangeCheckbox"
                      checked={values?.changePasswordAtNextLogin}
                      onChange={(value) => {
                        setFieldValue('changePasswordAtNextLogin', value.target.checked)
                        setActionData({
                          ...actionData,
                          changePasswordAtNextLogin: value.target.checked,
                        })
                      }}
                      disabled={false}
                      label={
                        values?.changePasswordAtNextLogin === true ? (
                          intl.formatMessage({
                            id: 'ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD',
                          })
                        ) : (
                          <>
                            <p>
                              {intl.formatMessage({
                                id: 'ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD',
                              })}
                            </p>
                            <p className="font-color-danger">
                              {intl.formatMessage({
                                id: 'ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD_DETAIL',
                              })}
                            </p>
                          </>
                        )
                      }
                    />
                  </Form.Group>

                  <Form.Group id="sendPasswordOnCompletion">
                    <Form.Check
                      className="custom-checkbox-reset-password"
                      custom
                      name="sendPasswordOnCompletion"
                      value={values?.sendPasswordTo}
                      type="checkbox"
                      id="sendPassword"
                      checked={values.sendPasswordTo}
                      onChange={(value) => {
                        setFieldValue('sendPasswordTo', value.target.checked)
                        setActionData({
                          ...actionData,
                          sendPasswordTo: value.target.checked,
                        })
                      }}
                      disabled
                      label={intl.formatMessage({
                        id: 'ENGAGE.SEND_PASSWORD_ON_COMPLETE',
                      })}
                    />
                  </Form.Group>

                  {values?.sendPasswordTo ? (
                    <div className="custom-switch-reset-password">
                      <div id="sendPasswordToggle" className="flex">
                        <p
                          className="pe-3"
                          style={{
                            color: isSendingPasswordToMe ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                          }}
                        >
                          <FormattedMessage id="ENGAGE.TO_ME" />
                        </p>
                        <Toggle
                          onChange={() =>
                            isSendingPasswordToMe ? setSendingPasswordToMe(false) : setSendingPasswordToMe(true)
                          }
                          checked={!isSendingPasswordToMe}
                          className="switch"
                          labelText=""
                          ariaLabel="send-password-toggle"
                        />
                        <p
                          className="ps-3"
                          style={{
                            color: !isSendingPasswordToMe ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                          }}
                        >
                          {intl
                            ? intl.formatMessage(
                                { id: 'ENGAGE.TO_AN_EMPLOYEE_AT_TENANT' },
                                { tenant: selectedCustomer.customer_name },
                              )
                            : ''}
                        </p>
                      </div>

                      {isSendingPasswordToMe && (
                        <Form.Group id="email">
                          <Form.Control
                            type="text"
                            className="inline font-bolder"
                            name="email"
                            placeholder={
                              intl
                                ? intl.formatMessage({
                                    id: 'PLACEHOLDER.ENTER_EMAIL',
                                  })
                                : ''
                            }
                            disabled
                            value={values?.email}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ')
                              setFieldValue('email', val.trim())
                              setFieldTouched('email', true, false)
                            }}
                          />
                        </Form.Group>
                      )}

                      {!isSendingPasswordToMe && (
                        <Select
                          id="employeeSelect"
                          ref={selectedEmployeeReference}
                          name="employee"
                          className="inline"
                          styles={selectStyle}
                          options={employeeOptions.filter((employee) => employee.email !== selectedEmployee?.email)}
                          onChange={(value) => {
                            setFieldValue('employee', value)
                            setActionData({
                              ...actionData,
                              employee: value,
                            })
                            checkSelection(value, setShowFindEmployeeModal, selectedEmployeeReference)
                          }}
                          getOptionValue={(value) => value}
                          isClearable
                          onBlur={() => setFieldTouched('employee', true)}
                          placeholder={
                            intl
                              ? intl.formatMessage({
                                  id: 'PLACEHOLDER.SELECT_EMPLOYEE',
                                })
                              : ''
                          }
                          components={{
                            MenuList: ScrollMenu,
                            // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
                            Option: (props) => <Option {...props} optionType="employee" />,
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    ''
                  )}
                </Col>
              </Row>
            </Modal.Body>

            {/* Footer Child */}
            {cloneChildComponents(
              {
                ...rest,
                handleChange,
                setFieldValue,
                setFieldTouched,
                values,
                formikRef,
              },
              children,
            )}
          </Form>
        )}
      </Formik>
    </PerfectScrollbar>
  )
}

export default ResetPasswordRenderBody
